import { Suspense, useMemo, useState } from "react";
import { useForm } from "react-hook-form";
import CustomAccordion from "../../../../components/ui/materialui/customAccordion";
import { useDataFetch } from "../../../../hooks/useDataFetch2";
import { useColumnsMorososServinco } from "../utils/columnsPac";
import Tabla from "../../../../components/ui/materialui/serverSideTable";
import TableHeader from "../../../../components/ui/materialui/tableHeader";
import MButton from "../../../../components/inputs/yup/materialui/mButton";
import SummarizeTwoToneIcon from '@mui/icons-material/SummarizeTwoTone';
import { FormFiltersInterface } from "../interfaces/FormFiltersInterface";
import { exportExcel } from "../../../../utils/exportExcel";
import { Box, Grid2, Typography } from "@mui/material";
import InputSelect from "../../../../components/inputs/yup/materialui/inputSelect";
import { selectorTransversal } from "../../../../dtos/selector";
import { grabarEmpresaCobranza } from "../services/morososServinco";
import { useToast } from "../../../../hooks/useToast";
import InfoIcon from '@mui/icons-material/Info';
import { PermissionGuard } from "../../../../components/auth/ProtectedRoute";
import { TipoFuncionalidad } from "../../../../utils/enums";

const urlTabla = `siscred-api-cartera-total/v1/morosos/morosos-servinco`;

interface TablaComponentProps {
    form: FormFiltersInterface;
    optionsEmpresaCobranza: selectorTransversal[];
};

interface FormEmpresaCobranza {
    idEmpresaCobranza: string;
};

export const TablaComponent = ({ form, optionsEmpresaCobranza }: TablaComponentProps ) => {

    const { addToast } = useToast();
    const [ ids, setIds ] = useState<number[]>([]);

    const { control, watch } = useForm<FormEmpresaCobranza>({
        defaultValues: {
            idEmpresaCobranza: '',
        }
    })

    const idEmpresaCobranza = watch("idEmpresaCobranza");
    
    const params = useMemo(() => {
        const p = new URLSearchParams();
        if( form.rutDeudor ) p.set("rutDeudor", String(form.rutDeudor.replaceAll('.', '')));
        if( form.numeroOperacion ) p.set("numeroOperacion", String(form.numeroOperacion));
        if( form.nombreDeudor ) p.set("nombreDeudor", String(form.nombreDeudor));
        if( form.rutEmisor && form.rutEmisor !== "0" ) p.set("rutEmisor", String(form.rutEmisor));
        if( form.isAdmin ) p.set("isAdmin", String(form.isAdmin));
        return p;
    }, [form]);


    const dataTable = useDataFetch(
        urlTabla,
        params,
        true,
    );

    const { COLUMNS } = useColumnsMorososServinco({ controlTabla: dataTable, ids, setIds });
    const columns = useMemo(() => COLUMNS, [dataTable]);

    const grabarOperacion = async () => {

        if( ids.length === 0 ) return;
        const response = await grabarEmpresaCobranza(form, idEmpresaCobranza, ids);

        if(response.status === 200){
            showToast('Morosos', 'Success', 'El proceso de grabado se realizó con éxito.');
            setIds([]);
            await dataTable.fetchData();
        };
    }

    const showToast = (title: string, tipo: "Success" | "Danger" | "Warning" | "Info" | "Dark" | "Light", mensaje: string) => {
        addToast({
          mostrar: true,
          titulo: title,
          tipo,
          mensaje,
        });
    }

    return (
        <CustomAccordion title={'Morosos'} expandedDefault>
            <Suspense>
                <Box sx={{
                        display: "flex",
                        justifyContent: "space-between",
                        alignItems: "center",
                        mb: 1,
                        gap: 2
                    }}
                >
                    <Box
                        sx={{
                            display: "flex",
                            alignItems: "center",
                            gap: 1,
                        }}
                    >
                        <InfoIcon
                            color="info"
                            sx={{ fontSize: 20, mt: "2px",}}
                        />
                            <Typography
                                variant="h6"
                                sx={{
                                    fontSize: "14px",
                                    lineHeight: 1.3,
                                }}
                        >
                            Los Mutuos seleccionados no serán agregados al archivo de morosos de Empresa de Cobranza.
                        </Typography>
                    </Box>
                    
                    <Grid2 size={5} sx={{ minWidth: 300, maxWidth: 420 }}>
                        <InputSelect
                            clearable
                            name="idEmpresaCobranza"
                            control={control}
                            label="Empresa Cobranza"
                            options={optionsEmpresaCobranza}
                        />
                    </Grid2>
                </Box>
                <Tabla
                    columns={columns}
                    control={dataTable}
                    url={urlTabla}
                    titulo={
                        <TableHeader
                            title=""
                            control={dataTable}
                            button={
                                <PermissionGuard permissions={[TipoFuncionalidad.Cobranza_adm]}>
                                <MButton
                                    type="button"
                                    variant="contained"
                                    onClick={grabarOperacion}
                                    startIcon={<SummarizeTwoToneIcon />}
                                    disabled={!ids.length}
                                >
                                    ({ ids.length }) Grabar
                                </MButton>
                                </PermissionGuard>
                            }
                            buttonExport={
                                <PermissionGuard permissions={[TipoFuncionalidad.Transversal_exportar]}>
                                <MButton
                                    type="button"
                                    variant="outlined"
                                    onClick={() => exportExcel(dataTable.data, columns, "Morosos")}
                                    startIcon={<SummarizeTwoToneIcon />}
                                >
                                    Exportar
                                </MButton>
                                </PermissionGuard>
                            }
                        />
                    }
                />
            </Suspense>
        </CustomAccordion>
    )
}
